import { ManagementCompoundComponent } from "./ManagementCompoundComponent";

export interface ManagementCompound {
  id: string;
  name?: string;
  description?: string;
  image?: string;
  product_brand_name?: string;
  vat_rate?: number;
  configured_base_cost_price?: number;
  configured_base_discount_rate?: number;
  configured_base_discount_value?: number;
  configured_base_retail_price?: number;
  configured_reseller_recommended_margin?: number;
  configured_reseller_gross_gain_level?: number;
  configured_reseller_gross_gain_value?: number;
  configured_price_pvp_with_vat?: number;
  configured_price_pvp_without_vat?: number;
  configured_price_at?: string;
  erp_synced_at?: string;
  erp_base_cost_price?: number;
  erp_base_discount_rate?: number;
  erp_base_discount_value?: number;
  erp_base_retail_price?: number;
  erp_reseller_recommended_margin?: number;
  erp_reseller_gross_gain_level?: number;
  erp_reseller_gross_gain_value?: number;
  erp_price_pvp_with_vat?: number;
  erp_price_pvp_without_vat?: number;
  available_units?: number;
  erp_available_units?: number;
  version?: number;
  category_slug?: string;
  category_name?: string;
  related_compounds?: string[];
  status?: "DRAFT" | "PENDING" | "PUBLISHED" | "HIDDEN" | "ARCHIVED";
  tags?: string[];
  /** Admin catalog: compound is in an ACTIVE (non-blocked) campaign. */
  in_campaign?: boolean;
  campaign_ids?: number[];
  components_list?: ManagementCompoundComponent[];
  price_strategy?: "configured" | "erp";

  // Price mismatch checking
  check_price_mismatch?: boolean | null;
  components_check_price_mismatch?: string[] | null;
  check_price_log?: CheckPriceMismatch[] | null;

  // AI-generated description lifecycle
  ai_description?: string | null;
  ai_description_status?: "PENDING" | "GENERATED" | "APPROVED" | "REJECTED";

  check_price(): CheckPriceMismatch | undefined;
  getBaseComponentList(): ManagementCompoundComponent[];
  getComponentListByType(
    type: ManagementComponentTypeKey,
  ): ManagementCompoundComponent[];
  getComponentsMap(): Map<
    ManagementComponentTypeKey,
    ManagementCompoundComponent[]
  >;
  getBaseComponentsCostPrice(type?: "configured" | "erp"): number;
  getBaseComponentsRev4Price(type?: "configured" | "erp"): number;
  getBaseComponentsResellerPrice(type?: "configured" | "erp"): number;
  getBaseDiscountValue(type?: "configured" | "erp"): number;
  getBaseDiscountRate(type?: "configured" | "erp"): number;
  getResellerRecommendedMargin(type?: "configured" | "erp"): number;
  getResellerGrossGainLevel(type?: "configured" | "erp"): number;
  getResellerGrossGainValue(type?: "configured" | "erp"): number;
  getPricePVPWithoutVat(type?: "configured" | "erp"): number;
  getPricePVPWithVat(type?: "configured" | "erp"): number;
  getAvailableUnits(type?: "configured" | "erp"): number;
  getComponentTypesWithoutOptional(): ManagementComponentTypeKey[];
  getComponentTypesWithOptional(): ManagementComponentTypeKey[];
  getComponentTypesWithProducts(): ManagementComponentTypeKey[];
}

export function NewManagementCompound(
  data: ManagementCompound,
): ManagementCompound {
  return {
    ...data,

    check_price: function (): CheckPriceMismatch | undefined {
      return data.check_price_log?.filter((c) => c.component_id == null).pop();
    },

    getBaseComponentList(): ManagementCompoundComponent[] {
      return this.components_list?.filter(
        (c) => c.base_component,
      ) as ManagementCompoundComponent[];
    },
    getComponentListByType(
      type: ManagementComponentTypeKey,
    ): ManagementCompoundComponent[] {
      return this.components_list?.filter(
        (c) => c.component_type_key === type,
      ) as ManagementCompoundComponent[];
    },
    getComponentsMap(): Map<
      ManagementComponentTypeKey,
      ManagementCompoundComponent[]
    > {
      const map = new Map<
        ManagementComponentTypeKey,
        ManagementCompoundComponent[]
      >();
      this.components_list?.forEach((c) => {
        const list = map.get(c.component_type_key) ?? [];
        map.set(c.component_type_key, [...list, c]);
      });
      return map;
    },
    getBaseComponentsCostPrice(type?: "configured" | "erp"): number {
      if (type === "erp") {
        return this.erp_base_cost_price ?? 0;
      }
      return this.configured_base_cost_price ?? 0;
    },
    getBaseComponentsRev4Price(type?: "configured" | "erp"): number {
      if (type === "erp") {
        return (
          (this.erp_base_retail_price ?? 0) +
          Math.abs(this.erp_base_discount_value ?? 0)
        );
      }
      return (
        (this.configured_base_retail_price ?? 0) -
        Math.abs(this.configured_base_discount_value ?? 0)
      );
    },
    getBaseComponentsResellerPrice(type?: "configured" | "erp"): number {
      if (type === "erp") {
        return this.erp_base_retail_price ?? 0;
      }
      return this.configured_base_retail_price ?? 0;
    },
    getBaseDiscountValue(type?: "configured" | "erp"): number {
      if (type === "erp") {
        return this.erp_base_discount_value ?? 0;
      }
      return this.configured_base_discount_value ?? 0;
    },
    getBaseDiscountRate(type?: "configured" | "erp"): number {
      if (type === "erp") {
        return this.erp_base_discount_rate ?? 0;
      }
      return this.configured_base_discount_rate ?? 0;
    },
    getResellerRecommendedMargin(type?: "configured" | "erp"): number {
      if (type === "erp") {
        return this.erp_reseller_recommended_margin ?? 0;
      }
      return this.configured_reseller_recommended_margin ?? 0;
    },
    getResellerGrossGainLevel(type?: "configured" | "erp"): number {
      if (type === "erp") {
        return this.erp_reseller_gross_gain_level ?? 0;
      }
      return this.configured_reseller_gross_gain_level ?? 0;
    },
    getResellerGrossGainValue(type?: "configured" | "erp"): number {
      if (type === "erp") {
        return this.erp_reseller_gross_gain_value ?? 0;
      }
      return this.configured_reseller_gross_gain_value ?? 0;
    },
    getPricePVPWithoutVat(type?: "configured" | "erp"): number {
      if (type === "erp") {
        return this.erp_price_pvp_without_vat ?? 0;
      }
      return this.configured_price_pvp_without_vat ?? 0;
    },
    getPricePVPWithVat(type?: "configured" | "erp"): number {
      if (type === "erp") {
        return this.erp_price_pvp_with_vat ?? 0;
      }
      return this.configured_price_pvp_with_vat ?? 0;
    },
    getAvailableUnits(type?: "configured" | "erp"): number {
      if (type === "erp") {
        return this.erp_available_units ?? 0;
      }
      return this.available_units ?? 0;
    },
    getComponentTypesWithoutOptional(): ManagementComponentTypeKey[] {
      return Array.from(
        new Set(
          (this.components_list ?? [])
            .filter((c) => !c.optional)
            .map((c) => c.component_type_key),
        ),
      );
    },
    getComponentTypesWithOptional(): ManagementComponentTypeKey[] {
      return Array.from(
        new Set(
          (this.components_list ?? [])
            .filter((c) => c.optional)
            .map((c) => c.component_type_key),
        ),
      );
    },
    getComponentTypesWithProducts(): ManagementComponentTypeKey[] {
      return Array.from(
        new Set(
          (this.components_list ?? [])
            .filter((c) => c.product_reference)
            .map((c) => c.component_type_key),
        ),
      );
    },
  };
}
